import { FC, useState, useEffect, StrictMode, useContext, useMemo } from 'react';
import Navigation from './navigation/Navigation';
import { NavigationContext } from './navigation/NavigationContext';
import { AppContext, AppProps } from './window';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router';
import { Suspense } from 'react';
import { lazyWithRetry as lazy } from './utils/chunkLoad';
import Notification, { NotificationData, normalizeNotificationLevel } from './libs/Notification';
import { useTranslation } from './i18n';
const ChatApp = lazy(() => import('./chat/ChatApp'));
const UserProfileFromQuery = lazy(() => import('./user/UserProfile').then(m => ({ default: m.UserProfileFromQuery })));
// Prefetched: members hit the payment chunk at paywall moments, often mid-flow
const Payment = lazy(() => import(/* webpackPrefetch: true */ './psp/Payment'));
const PaymentSuccess = lazy(() => import('./psp/PaymentSuccess'));
const PaymentError = lazy(() => import('./psp/PaymentError'));
const PaymentReturn = lazy(() => import('./psp/PaymentReturn'));
const Search = lazy(() => import('./user/Search'));
const Settings = lazy(() => import('./user/Settings'));
const NotificationSettings = lazy(() => import('./user/NotificationSettings'));
const Favorites = lazy(() => import('./user/Favorites').then(m => ({ default: m.Favorites })));
const Visitors = lazy(() => import('./user/Visitors').then(m => ({ default: m.Visitors })));
const Slider = lazy(() => import('./slider/App').then(m => ({ default: m.Slider })));
import { AuthorizedUser } from './models/User';
import { WrapperContext } from './context/WrapperContext';
const AboutPage = lazy(() => import('./about/AboutPage'));
// Eagerly import home/landing to avoid Suspense fallback as LCP on mobile
import HomePage from './about/HomePage';
// Small: the site itself is a lazy chunk per domain (src/sites/<domain>)
import CustomFrontendPage from './customFrontend/CustomFrontendPage';
import LandingPageA from './landing/LandingPageA';
import LandingPageB from './landing/LandingPageB';
import { LandingPageC } from './landing/LandingPageC';
import LandingPageD from './landing/LandingPageD';
import PendingPasswordPrompt from './landing/PendingPasswordPrompt';
import { ProfileCompletionProvider } from './user/profile-completion/ProfileCompletionContext';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import AppErrorFallback from './components/AppErrorFallback';
import DisclaimerModal from './components/DisclaimerModal';
import { useDisclaimer } from './hooks/useDisclaimer';
import { ThemeProvider } from './context/ThemeContext';
import { checkReactDevMode } from './utils/checkDevMode';
// Import all styles through single entry point
import './../src/sass/main.scss';

import { NotificationContext } from './context/NotificationContext';
import { ConversationProvider } from './context/ConversationContext';
import { ArchiveProvider } from './context/ArchiveContext';
import { SocketProvider } from './context/SocketContext';
import { PendingPurchaseProvider } from './context/PendingPurchaseContext';
import { PaymentSuccessProvider } from './context/PaymentSuccessContext';
import { TopUpProvider } from './context/TopUpContext';
import ChatSocketManager from './socket/ChatSocketManager';
import VisitorSocketManager from './socket/VisitorSocketManager';
import TitleComponent from './components/TitleComponent';
import UserSocketManager from './socket/UserSocketManager';
import PushNotificationPermissionChecker from './components/PushNotificationPermissionChecker';
import MemberRoute from './components/MemberRoute';
import Clarity from '@microsoft/clarity';
import { useClarityIdentification } from './hooks/useClarityIdentification';
import OrientationModal from './components/OrientationModal';

//Temporary hardcode for localsexencounters.com
const projectId = "sha7pmt0g0";
// Component to handle initial notifications and redirects
interface InitialStateHandlerProps {
  notifications?: AppProps['notifications'] | undefined;
  redirectRoute?: string | undefined;
  redirectParams?: Record<string, any> | undefined;
  addNotification: (data: NotificationData) => void;
}
const InitialStateHandler: FC<InitialStateHandlerProps> = ({ notifications, redirectRoute, redirectParams, addNotification }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Process initial notifications from PHP
    if (notifications && notifications.length > 0) {
      notifications.forEach(notification => {
        const notificationData: NotificationData = {
          level: normalizeNotificationLevel(notification.type),
          message: notification.message
        };
        
        // Only include timeout if it's less than 2000ms
        if (notification.duration && notification.duration < 2000) {
          notificationData.timeout = notification.duration;
        }
        
        addNotification(notificationData);
      });
    }

    // Handle redirect route from PHP
    if (redirectRoute) {
      const routeMap: Record<string, string> = {
        '/site/index': '/',
        '/user/credentials/activate': '/',
        '/user/credentials/login': '/',
        '/user/credentials/form': '/',
        '/user/credentials/redeem': '/',
        '/user/credentials/reset-password': '/',
        '/user/credentials/mail': '/settings',
        '/user/credentials/unsub': '/settings',
        '/user/profile/index': '/profile',
        '/user/profile': '/profile',
        '/chat/conversation/v2': '/inbox',
        '/psp/default/index': '/credits'
      };

      let reactRoute = routeMap[redirectRoute] || redirectRoute;
      
      // Handle special cases with parameters
      if (redirectRoute === '/chat/conversation/v2' && redirectParams?.['uid']) {
        reactRoute = `/inbox/${redirectParams['uid']}`;
      } else if (redirectRoute === '/user/profile' && redirectParams?.['id']) {
        reactRoute = `/user/${redirectParams['id']}`;
      }
      
      navigate(reactRoute);
    }
  }, []);

  return null;
};

// Component to conditionally render navigation
const NavigationWrapper: FC = () => {
  const location = useLocation();
  const isLandingPage = location.pathname.startsWith('/l/');
  const { user } = useContext(AppContext);
  const { showDisclaimer, acceptDisclaimer } = useDisclaimer(user);

  return (
    <>
      <header className="sticky-top">
        <Navigation
          landingPageMode={isLandingPage}
          hideLogin={true}
        />
      </header>
      {/*
        The age/cookie/T&C disclaimer is shown to guest visitors on regular
        pages (home, etc.). It is intentionally suppressed on landing pages
        (`/l/*`) because those flows fold the same consent into the
        `auth.tos_agree` signup checkbox. Logged-in users never see it -- the
        useDisclaimer hook short-circuits when `user` is set.
      */}
      {!isLandingPage && (
        <DisclaimerModal
          show={showDisclaimer}
          onAccept={acceptDisclaimer}
        />
      )}
    </>
  );
};

// Component to conditionally render footer
const FooterWrapper: FC = () => {
  const location = useLocation();
  const hideFooter = location.pathname.startsWith('/inbox') || location.pathname === '/match' || location.pathname === '/search';
  
  return !hideFooter ? <Footer /> : null;
};

// Removed memberRoute helper in favor of explicit <Route> definitions with MemberRoute wrapper

const App: FC<AppProps> = (props) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<AuthorizedUser | undefined>(props.user);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [hideSecondNavbar, setHideSecondNavbar] = useState(false);
  const [hideFirstNavbar, setHideFirstNavbar] = useState(false);
  const [wrapperClasses, setWrapperClasses] = useState<string>('');
  // One-time search form auto-close flag
  const [searchFormAutoClosedOnce, setSearchFormAutoClosedOnce] = useState<boolean>(props.searchFormAutoClosedOnce ?? false);
  // Optionally allow overriding search click
  const [onSearchClick, setOnSearchClick] = useState<(() => void) | undefined>(undefined);
  
  // Use Clarity identification hook
  useClarityIdentification();
  // Defer Clarity init to idle to avoid impacting LCP/FCP on mobile
  useEffect(() => {
    const init = () => {
      try { Clarity.init(projectId); } catch {}
    };
    // @ts-ignore
    if (typeof window.requestIdleCallback === 'function') {
      // @ts-ignore
      const id = window.requestIdleCallback(init, { timeout: 1000 });
      return () => {
        // @ts-ignore
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(id);
      };
    }
    const t = setTimeout(init, 300);
    return () => clearTimeout(t);
  }, []);
  
  // Log React development mode status on app initialization
  useEffect(() => {
    checkReactDevMode();
  }, []);

  const addNotification = (data: NotificationData) => {
    setNotifications((prev) => [...prev, data]);
  };

  const removeNotification = (index: number) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  const appContextValue = useMemo(() => ({ 
    ...props, 
    user: user as any, 
    setUser, 
    setHideSecondNavbar,
    searchFormAutoClosedOnce,
    setSearchFormAutoClosedOnce,
  }), [props, user, searchFormAutoClosedOnce]);
  // Expose raw onSearchClick (can be undefined). Navigation uses its absence to perform default routing.
  const navigationContextValue = useMemo(() => ({ 
    hideFirstNavbar, 
    setHideFirstNavbar,
    hideSecondNavbar, 
    setHideSecondNavbar, 
    onSearchClick, 
    setOnSearchClick 
  }), [hideFirstNavbar, hideSecondNavbar, onSearchClick]);
  const notificationContextValue = useMemo(() => ({ notifications, addNotification, removeNotification }), [notifications]);
  const wrapperContextValue = useMemo(() => ({ wrapperClasses, setWrapperClasses }), [wrapperClasses]);

  const appContent = (
  <AppContext.Provider value={appContextValue}>
      <ThemeProvider>
        <SocketProvider>
          <NavigationContext.Provider value={navigationContextValue}>
            <NotificationContext.Provider value={notificationContextValue}>
              <WrapperContext.Provider value={wrapperContextValue}>
                <ConversationProvider>
                  <ArchiveProvider>
                  <div className={`d-flex flex-column flex-grow-1 vh-100 ${wrapperClasses}`}>
                  <Router>
                    <PendingPurchaseProvider>
                      <PaymentSuccessProvider>
                      <TopUpProvider>
                        <InitialStateHandler 
                          notifications={props.notifications}
                          redirectRoute={props.redirectRoute as any}
                          redirectParams={props.redirectParams}
                          addNotification={addNotification}
                        />
                      
                      {user && <ChatSocketManager />}
                      {user && <UserSocketManager />}
                      {user && <VisitorSocketManager />}
                      <TitleComponent />
                      <PushNotificationPermissionChecker />
                      {/* Without it any render error unmounts the whole root (a blank page) */}
                      <ErrorBoundary fallbackComponent={AppErrorFallback} reloadOnChunkError>
                      <NavigationWrapper />
                      <Notification
                        className="global-notifications global-notifications--overlay-top"
                        notifications={notifications}
                        onDismiss={removeNotification}
                        defaultTimeout={5000}
                      />
                      <OrientationModal />
                      <PendingPasswordPrompt />
                      <Suspense fallback={<div className="py-3" aria-label={t('general.loading')} /> }>
                        <Routes>
                          {/* A custom frontend (src/sites/<domain>) renders / and its own paths; every other whitelabel the generic home page */}
                          {props.customFrontend
                            ? props.customFrontend.pages.map((page) => (
                              <Route key={page.path} path={page.path} element={<CustomFrontendPage site={props.customFrontend!} page={page} />} />
                            ))
                            : <Route path="/" element={<HomePage />} />}
                          <Route path="/l/a" element={<LandingPageA />} />
                          <Route path="/l/b" element={<LandingPageB />} />
                          <Route path="/l/c" element={<LandingPageC />} />
                          <Route path="/l/d" element={<LandingPageD />} />
                          <Route path="/profile" element={<MemberRoute><ProfileCompletionProvider><Settings /></ProfileCompletionProvider></MemberRoute>} />
                          <Route path="/settings" element={<MemberRoute><ProfileCompletionProvider><NotificationSettings /></ProfileCompletionProvider></MemberRoute>} />
                          <Route path="/search" element={<MemberRoute><ProfileCompletionProvider><Search /></ProfileCompletionProvider></MemberRoute>} />
                          <Route path="/inbox" element={<MemberRoute><ChatApp /></MemberRoute>} />
                          <Route path="/inbox/:userId" element={<MemberRoute><ChatApp /></MemberRoute>} />
                          <Route path="/user/:id" element={<MemberRoute><UserProfileFromQuery /></MemberRoute>} />
                          <Route path="/credits" element={<MemberRoute><Payment /></MemberRoute>} />
                          <Route path="/psp/default/success" element={<MemberRoute><PaymentSuccess /></MemberRoute>} />
                          <Route path="/psp/default/error" element={<MemberRoute><PaymentError /></MemberRoute>} />
                          <Route path="/psp/paynl/return" element={<MemberRoute><PaymentReturn /></MemberRoute>} />
                          <Route path="/psp/dial-xs/return" element={<MemberRoute><PaymentReturn /></MemberRoute>} />
                          <Route path="/psp/comm-doo/return" element={<MemberRoute><PaymentReturn /></MemberRoute>} />
                          <Route path="/match" element={<MemberRoute><Slider /></MemberRoute>} />
                          <Route path="/favorites" element={<MemberRoute><Favorites /></MemberRoute>} />
                          <Route path="/visitors" element={<MemberRoute><Visitors /></MemberRoute>} />
                          <Route path="/about" element={<AboutPage />} />
                          {/* Legacy/unknown URLs must never render an empty page */}
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </Suspense>
                    <FooterWrapper />
                      </ErrorBoundary>
                      </TopUpProvider>
                      </PaymentSuccessProvider>
                    </PendingPurchaseProvider>
                  </Router>
                </div>
                </ArchiveProvider>
              </ConversationProvider>
            </WrapperContext.Provider>
          </NotificationContext.Provider>
        </NavigationContext.Provider>
      </SocketProvider>
    </ThemeProvider>
    </AppContext.Provider>
  );
  
  // Wrap in StrictMode for development
  if (process.env['NODE_ENV'] === 'development') {
    return <StrictMode>{appContent}</StrictMode>;
  }
  
  return appContent;
};

export default App;
